import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Avatar,
  Chip,
  Divider,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import EngineeringIcon from "@mui/icons-material/Engineering";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import CategoryIcon from "@mui/icons-material/Category";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import BusinessIcon from "@mui/icons-material/Business";
import DescriptionIcon from "@mui/icons-material/Description";
import SettingsIcon from "@mui/icons-material/Settings";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { TYPE_ICONS } from "../constants";
import { calculateStock, pluralizeUnit } from "../utils";

/**
 * Panel lateral con distribución detallada del stock
 */
const StockDistributionPanel = ({ product, onEdit, onTransfer, onConsume, onReceive, selectedTechnician, onEditMinStock }) => {
  if (!product) return null;

  const { total, warehouse, technicians, warehousePct } =
    calculateStock(product);
  const isLowStock = warehouse < (product.minStock || 0);

  return (
    <Stack spacing={2.5}>
      {/* Card Principal: Info del Producto */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
          border: "2px solid",
          borderColor: "primary.main",
        }}
      >
        <Box
          sx={{
            bgcolor: "primary.main",
            color: "white",
            p: 2.5,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: "rgba(255,255,255,0.2)",
              }}
            >
              {TYPE_ICONS[product.type] || <CategoryIcon fontSize="large" />}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} noWrap>
                {product.name}
              </Typography>
              <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap" gap={0.5}>
                <Chip
                  label={product.sku}
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    color: "white",
                    height: 22,
                  }}
                />
                {product.brand && (
                  <Chip
                    label={product.brand}
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.15)",
                      color: "white",
                      height: 22,
                    }}
                  />
                )}
              </Stack>
            </Box>
          </Stack>
        </Box>

        <CardContent sx={{ p: 2.5 }}>
          {/* Acciones Rápidas */}
          <Stack spacing={1} mb={3}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<SwapHorizIcon />}
                onClick={() => onTransfer(product)}
                fullWidth
                size="large"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Transferir
              </Button>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => onEdit(product)}
                fullWidth
                size="large"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Editar
              </Button>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="success"
                startIcon={<AddShoppingCartIcon />}
                onClick={() => onReceive(product)}
                fullWidth
                size="large"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Recibir Stock
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => onConsume(product)}
                fullWidth
                size="large"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Consumir
              </Button>
            </Stack>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {/* Información Básica */}
          <List dense disablePadding>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CategoryIcon color="action" />
              </ListItemIcon>
              <ListItemText
                primary="Categoría"
                secondary={product.category || "Sin categoría"}
                primaryTypographyProps={{
                  variant: "caption",
                  color: "text.secondary",
                }}
                secondaryTypographyProps={{
                  variant: "body2",
                  fontWeight: 600,
                  color: "text.primary",
                }}
              />
            </ListItem>

            {product.cofeprisCat && product.cofeprisCat !== "NA" && (
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <BusinessIcon color="action" />
                </ListItemIcon>
                <ListItemText
                  primary="COFEPRIS"
                  secondary={product.cofeprisCat}
                  primaryTypographyProps={{
                    variant: "caption",
                    color: "text.secondary",
                  }}
                  secondaryTypographyProps={{
                    variant: "body2",
                    fontWeight: 600,
                    color: "text.primary",
                  }}
                />
              </ListItem>
            )}

            {product.description && (
              <ListItem sx={{ px: 0, alignItems: "flex-start" }}>
                <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                  <DescriptionIcon color="action" />
                </ListItemIcon>
                <ListItemText
                  primary="Descripción"
                  secondary={product.description}
                  primaryTypographyProps={{
                    variant: "caption",
                    color: "text.secondary",
                  }}
                  secondaryTypographyProps={{
                    variant: "body2",
                    color: "text.primary",
                  }}
                />
              </ListItem>
            )}
          </List>
        </CardContent>
      </Card>

      {/* Card: Distribución del Stock */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Typography variant="h6" fontWeight={700} mb={2.5}>
            Distribución del Stock
          </Typography>

          <Stack spacing={2.5}>
            {/* Total */}
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Stock Total
                </Typography>
                <Typography variant="h4" fontWeight={700} color="success.main">
                  {total}
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    ml={0.5}
                  >
                    {pluralizeUnit(product.unit, total)}
                  </Typography>
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={100}
                color="success"
                sx={{
                  height: 10,
                  borderRadius: 5,
                  bgcolor: "success.100",
                }}
              />
            </Box>

            <Divider />

            {/* Almacenes */}
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <WarehouseIcon color="primary" fontSize="small" />
                  <Typography variant="body2" fontWeight={600}>
                    Almacenes ({product.warehouses?.length || 0})
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Typography variant="h5" fontWeight={700} color="primary.main">
                    {warehouse}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    / {total} {pluralizeUnit(product.unit, total)}
                  </Typography>
                </Stack>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={warehousePct}
                color={isLowStock ? "error" : "primary"}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: "grey.200",
                }}
              />
              <Typography
                variant="caption"
                color={isLowStock ? "error.main" : "text.secondary"}
                fontWeight={600}
                display="block"
                mt={0.5}
              >
                {warehousePct}% del total
                {isLowStock && " - ⚠️ Bajo stock mínimo"}
              </Typography>
              
              {/* Desglose por almacén individual */}
              {product.warehouses && product.warehouses.length > 0 && (
                <Box sx={{ mt: 1.5, pl: 1 }}>
                  {product.warehouses.map((wh) => {
                    const isLowStockWh = wh.minStock > 0 && wh.stock < wh.minStock;
                    return (
                      <Stack
                        key={wh.locationId}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          py: 0.75,
                          px: 1.5,
                          mb: 0.5,
                          bgcolor: isLowStockWh ? "error.50" : "grey.50",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: isLowStockWh ? "error.200" : "grey.200",
                          flexWrap: 'wrap',
                          gap: 0.5,
                        }}
                      >
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="caption" color="text.secondary">
                            📦 {wh.locationName}
                          </Typography>
                          {isLowStockWh && (
                            <Typography variant="caption" color="error.main">
                              ⚠️
                            </Typography>
                          )}
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                          <Chip
                            label={`${wh.stock} ${pluralizeUnit(product.unit, wh.stock)}`}
                            size="small"
                            color={isLowStockWh ? "error" : wh.stock > 0 ? "primary" : "default"}
                            sx={{ height: 20, fontSize: "0.7rem" }}
                          />
                          {wh.minStock && wh.minStock > 0 ? (
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem", whiteSpace: 'nowrap' }}>
                              (mín: {wh.minStock})
                            </Typography>
                          ) : onEditMinStock ? (
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.6rem", fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                              Sin mínimo
                            </Typography>
                          ) : null}
                          {onEditMinStock && (
                            <Tooltip title={wh.minStock && wh.minStock > 0 ? "Editar stock mínimo" : "Configurar stock mínimo"}>
                              <IconButton
                                size="small"
                                onClick={() => onEditMinStock(wh, 'warehouse')}
                                sx={{ 
                                  padding: '2px',
                                  color: wh.minStock && wh.minStock > 0 ? 'action.active' : 'warning.main',
                                  '&:hover': {
                                    bgcolor: wh.minStock && wh.minStock > 0 ? 'action.hover' : 'warning.50',
                                  }
                                }}
                              >
                                <SettingsIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </Stack>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* Técnicos */}
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <EngineeringIcon color="info" fontSize="small" />
                  <Typography variant="body2" fontWeight={600}>
                    Con Técnicos
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Typography variant="h5" fontWeight={700} color="info.main">
                    {technicians}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    / {total} {pluralizeUnit(product.unit, total)}
                  </Typography>
                </Stack>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={total ? ((technicians / total) * 100) : 0}
                color="info"
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: "grey.200",
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={0.5}
              >
                {total ? Math.round((technicians / total) * 100) : 0}% del total
              </Typography>
              
              {/* Desglose por técnico individual */}
              {product.technicians && product.technicians.length > 0 && (
                <Box sx={{ mt: 1.5, pl: 1 }}>
                  {product.technicians.filter(t => t.stock > 0).map((tech) => {
                    const isLowStockTech = tech.minStock > 0 && tech.stock < tech.minStock;
                    return (
                      <Stack
                        key={tech.technicianId}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          py: 0.75,
                          px: 1.5,
                          mb: 0.5,
                          bgcolor: isLowStockTech ? "error.50" : "grey.50",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: isLowStockTech ? "error.200" : "grey.200",
                          flexWrap: 'wrap',
                          gap: 0.5,
                        }}
                      >
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="caption" color="text.secondary">
                            👷 {tech.technicianName}
                          </Typography>
                          {isLowStockTech && (
                            <Typography variant="caption" color="error.main">
                              ⚠️
                            </Typography>
                          )}
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                          <Chip
                            label={`${tech.stock} ${pluralizeUnit(product.unit, tech.stock)}`}
                            size="small"
                            color={isLowStockTech ? "error" : "info"}
                            sx={{ height: 20, fontSize: "0.7rem" }}
                          />
                          {tech.minStock && tech.minStock > 0 ? (
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem", whiteSpace: 'nowrap' }}>
                              (mín: {tech.minStock})
                            </Typography>
                          ) : onEditMinStock ? (
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.6rem", fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                              Sin mínimo
                            </Typography>
                          ) : null}
                          {onEditMinStock && (
                            <Tooltip title={tech.minStock && tech.minStock > 0 ? "Editar stock mínimo" : "Configurar stock mínimo"}>
                              <IconButton
                                size="small"
                                onClick={() => onEditMinStock(tech, 'technician')}
                                sx={{ 
                                  padding: '2px',
                                  color: tech.minStock && tech.minStock > 0 ? 'action.active' : 'warning.main',
                                  '&:hover': {
                                    bgcolor: tech.minStock && tech.minStock > 0 ? 'action.hover' : 'warning.50',
                                  }
                                }}
                              >
                                <SettingsIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </Stack>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* Residuos Peligrosos */}
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <DeleteForeverIcon color="warning" fontSize="small" />
                  <Typography variant="body2" fontWeight={600}>
                    Residuos Peligrosos
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Typography variant="h5" fontWeight={700} color="warning.main">
                    {product.hazardousStock || 0}
                  </Typography>
                </Stack>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={100}
                color="warning"
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: "grey.200",
                }}
              />
            </Box>
          </Stack>

          {/* Stock Mínimo */}
          {product.minStock && (
            <>
              <Divider sx={{ my: 2.5 }} />
              <Box
                sx={{
                  bgcolor: isLowStock ? "error.50" : "success.50",
                  borderRadius: 2,
                  p: 1.5,
                  border: "1px solid",
                  borderColor: isLowStock ? "error.200" : "success.200",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  fontWeight={600}
                  mb={0.5}
                >
                  Stock Mínimo Configurado
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color={isLowStock ? "error.main" : "success.main"}
                >
                  {product.minStock} {pluralizeUnit(product.unit, product.minStock)}
                </Typography>
                {isLowStock && (
                  <Typography variant="caption" color="error.main" fontWeight={600}>
                    ⚠️ Se requiere reabastecimiento
                  </Typography>
                )}
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};

export default StockDistributionPanel;

